import { Component } from '@theme/component';
import { fetchConfig, debounce, resetShimmer } from '@theme/utilities';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

const CART_COMPONENT_SELECTOR = 'cart-items-component[data-section-id]';
const CART_CONTROL_SELECTOR = 'button, input, select, textarea';
const DRAWER_HYDRATION_KEY = 'cart-drawer-inner';
const HYDRATION_KEY_ATTRIBUTE = 'data-hydration-key';
const MORPH_PROOF_ATTRIBUTE = 'data-cart-reconciliation-proof';
let cartOperationPending = false;
let externalRefreshQueued = false;
let externalRefreshTimer;
let morphProofSequence = 0;
const handledExternalCartEvents = new WeakSet();

/**
 * @param {unknown} value
 * @returns {value is Record<string, any>}
 */
function isRecord(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Reads an HTTP response as a JSON object. Cart recovery is deliberately fail-closed:
 * HTTP failures, invalid JSON, and non-object payloads all reject before any DOM work.
 *
 * @param {Response} response
 * @returns {Promise<Record<string, any>>}
 */
async function readJSONResponse(response) {
  if (!response.ok) throw new Error('Cart request failed');

  const responseText = await response.text();
  let parsedResponse;

  try {
    parsedResponse = JSON.parse(responseText);
  } catch {
    throw new Error('Cart response was not valid JSON');
  }

  if (!isRecord(parsedResponse)) throw new Error('Cart response had an invalid shape');

  return parsedResponse;
}

/**
 * @param {HTMLElement} root
 * @param {string} sectionId
 * @returns {HTMLElement}
 */
function findExpectedCartComponent(root, sectionId) {
  const components = [];

  if (root.matches(CART_COMPONENT_SELECTOR) && root.dataset.sectionId === sectionId) components.push(root);
  components.push(
    ...Array.from(root.querySelectorAll(CART_COMPONENT_SELECTOR)).filter(
      (component) => component instanceof HTMLElement && component.dataset.sectionId === sectionId
    )
  );

  if (components.length !== 1 || !(components[0] instanceof HTMLElement)) {
    throw new Error(`Cart section ${sectionId} had an invalid cart component root`);
  }

  return components[0];
}

/**
 * Resolves the exact target this component is allowed to reconcile. Drawer updates are
 * restricted to the single reviewed hydration target so the drawer/dialog shell survives.
 *
 * @param {HTMLElement} sectionRoot
 * @param {{sectionId: string, isDrawer: boolean}} descriptor
 * @returns {HTMLElement}
 */
function resolveCartRenderTarget(sectionRoot, descriptor) {
  if (!descriptor.isDrawer) return findExpectedCartComponent(sectionRoot, descriptor.sectionId);

  const sectionComponent = findExpectedCartComponent(sectionRoot, descriptor.sectionId);
  const hydrationTarget = sectionComponent.closest(
    `[${HYDRATION_KEY_ATTRIBUTE}="${DRAWER_HYDRATION_KEY}"]`
  );
  if (!(hydrationTarget instanceof HTMLElement) || !sectionRoot.contains(hydrationTarget)) {
    throw new Error(`Cart section ${descriptor.sectionId} had an invalid drawer hydration target`);
  }

  const targetComponent = findExpectedCartComponent(hydrationTarget, descriptor.sectionId);
  if (targetComponent !== sectionComponent) {
    throw new Error(`Cart section ${descriptor.sectionId} had a cart component outside its hydration target`);
  }

  return hydrationTarget;
}

/**
 * Captures authoritative cart state that must match after the exact target morph.
 * The one-use proof attribute separately establishes that the returned target was applied.
 *
 * @param {HTMLElement} target
 * @param {{sectionId: string, isDrawer: boolean}} descriptor
 * @returns {string}
 */
function getCartTargetSignature(target, descriptor) {
  const component = findExpectedCartComponent(target, descriptor.sectionId);
  const itemCount = component.querySelector('[ref="cartItemCount"]')?.textContent?.trim() ?? '0';
  const rowState = Array.from(component.querySelectorAll('[ref="cartItemRows[]"]')).map((row) => ({
    key: row instanceof HTMLElement ? row.dataset.key ?? '' : '',
    parentKey: row instanceof HTMLElement ? row.dataset.parentKey ?? '' : '',
  }));
  const quantities = Array.from(component.querySelectorAll('input[data-cart-line]')).map((input) =>
    input instanceof HTMLInputElement ? `${input.dataset.cartLine ?? ''}:${input.value}` : ''
  );
  const normalizedText = component.textContent?.replace(/\s+/g, ' ').trim() ?? '';

  return JSON.stringify({
    target: target.tagName,
    hydrationKey: descriptor.isDrawer ? target.getAttribute(HYDRATION_KEY_ATTRIBUTE) : null,
    itemCount,
    rowState,
    quantities,
    empty: component.querySelector('.cart-items__empty-button') !== null,
    normalizedText,
  });
}

/**
 * @param {unknown} sections
 * @param {Array<{sectionId: string, isDrawer: boolean}>} descriptors
 * @returns {Map<string, {document: Document, returnedTarget: HTMLElement, expectedSignature: string}>}
 */
function validateSectionBundle(sections, descriptors) {
  if (!isRecord(sections)) throw new Error('Cart sections were missing or malformed');

  const validatedSections = new Map();

  for (const descriptor of descriptors) {
    const { sectionId } = descriptor;
    const sectionHTML = sections[sectionId];
    if (typeof sectionHTML !== 'string' || sectionHTML.trim() === '') {
      throw new Error(`Cart section ${sectionId} was missing`);
    }

    const sectionDocument = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const returnedSectionRoot = sectionDocument.getElementById(`shopify-section-${sectionId}`);
    const liveSectionRoot = document.getElementById(`shopify-section-${sectionId}`);
    if (!(returnedSectionRoot instanceof HTMLElement)) throw new Error(`Cart section ${sectionId} had no section root`);
    if (!(liveSectionRoot instanceof HTMLElement)) throw new Error(`Live cart section ${sectionId} had no section root`);

    const returnedTarget = resolveCartRenderTarget(returnedSectionRoot, descriptor);
    const liveTarget = resolveCartRenderTarget(liveSectionRoot, descriptor);
    if (returnedTarget.tagName !== liveTarget.tagName) {
      throw new Error(`Cart section ${sectionId} returned a mismatched reconciliation target`);
    }

    validatedSections.set(sectionId, {
      document: sectionDocument,
      returnedTarget,
      expectedSignature: getCartTargetSignature(returnedTarget, descriptor),
    });
  }

  return validatedSections;
}

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce((event) => this.#onQuantityChange(event), 300);
  #disabledControls = new Set();

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#queueQuantityChange);
    CartItemsComponent.#scheduleExternalRefresh();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#debouncedOnChange.cancel();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#queueQuantityChange);
  }

  /**
   * Drops quantity events while any mounted cart component is mutating or refreshing.
   * This wrapper runs before the debounce so a pending operation cannot queue a hidden
   * second mutation for later.
   *
   * @param {QuantitySelectorUpdateEvent} event
   */
  #queueQuantityChange = (event) => {
    if (cartOperationPending) return;
    this.#debouncedOnChange(event);
  };

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (cartOperationPending || !(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number.
    if (!line) return;

    if (quantity === 0) {
      this.onLineItemRemove(line);
      return;
    }

    void this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
  }

  /**
   * Handles the line item removal. The authoritative row stays mounted until the
   * Shopify response and every requested section have passed validation and morph.
   *
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    void this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @returns {Promise<void>}
   */
  async updateQuantity(config) {
    const operation = this.#prepareOperation();
    if (!operation || !this.#beginOperation(operation.descriptors)) return;

    const cartPerformanceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    let validationFocus;
    let showRefreshError = false;

    try {
      const { line, quantity } = config;
      const sections = operation.descriptors.map(({ sectionId }) => sectionId);
      const body = JSON.stringify({
        line,
        quantity,
        sections: sections.join(','),
        sections_url: window.location.pathname,
      });

      this.#hideRefreshError();
      this.#hideLineError(line);
      this.#startShimmer(line);

      const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
      const parsedResponse = await readJSONResponse(response);

      if (parsedResponse.errors) {
        validationFocus = this.#handleCartError(line, parsedResponse);
        return;
      }

      const validatedSections = validateSectionBundle(parsedResponse.sections, operation.descriptors);
      await this.#applySectionBundle(
        validatedSections,
        operation.descriptors,
        operation.snapshots
      );

      this.#publishAuthoritativeUpdate(parsedResponse, parsedResponse.sections, validatedSections);
    } catch {
      this.#restoreQuantityInput(config.line);
      showRefreshError = true;
    } finally {
      this.#finishOperation(operation.descriptors);

      if (validationFocus instanceof HTMLElement) validationFocus.focus();
      if (showRefreshError) this.#showRefreshError();

      cartPerformance.measureFromMarker(cartPerformanceUpdateMarker);
      CartItemsComponent.#scheduleExternalRefresh();
    }
  }

  /**
   * Refreshes mounted cart sections without replaying a quantity or remove mutation.
   * @param {Event} event
   * @returns {Promise<void>}
   */
  async retryCartRefresh(event) {
    event.preventDefault();
    externalRefreshQueued = false;
    await this.#refreshAuthoritativeSections();
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#queueExternalRefresh(event);
  };

  /**
   * Handles cart updates initiated outside this cart-items component.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.detail?.data?.source === 'cart-items-component') return;
    this.#queueExternalRefresh(event);
  };

  /**
   * Coalesces cart-update, cart-add, and discount events into an authoritative GET.
   * Supplied event HTML is never applied because it can predate an in-flight mutation.
   *
   * @param {Event} event
   */
  #queueExternalRefresh(event) {
    if (handledExternalCartEvents.has(event)) return;
    handledExternalCartEvents.add(event);
    externalRefreshQueued = true;
    CartItemsComponent.#scheduleExternalRefresh();
  }

  /**
   * @returns {CartItemsComponent | undefined}
   */
  static #findConnectedCoordinator() {
    return Array.from(document.querySelectorAll(CART_COMPONENT_SELECTOR)).find(
      (component) => component instanceof CartItemsComponent && component.isConnected
    );
  }

  /**
   * Uses a module-wide zero-delay task to fold events immediately before and after
   * reconciliation settlement. The disposable listener instance never owns execution:
   * a currently connected component is selected only when the task runs.
   */
  static #scheduleExternalRefresh() {
    if (cartOperationPending || externalRefreshTimer !== undefined || !externalRefreshQueued) return;

    externalRefreshTimer = setTimeout(() => {
      externalRefreshTimer = undefined;
      if (cartOperationPending || !externalRefreshQueued) return;

      const coordinator = CartItemsComponent.#findConnectedCoordinator();
      if (!coordinator) return;

      void coordinator.#refreshAuthoritativeSections({ consumeExternalQueue: true });
    }, 0);
  }

  /**
   * Runs one GET-only authoritative mounted-section refresh for retry or coalesced
   * external events. It never accepts or reconstructs a cart mutation payload.
   *
   * @param {{consumeExternalQueue?: boolean}} [options]
   * @returns {Promise<void>}
   */
  async #refreshAuthoritativeSections({ consumeExternalQueue = false } = {}) {
    const operation = this.#prepareOperation();
    if (!operation) return;
    if (!this.#beginOperation(operation.descriptors)) {
      CartItemsComponent.#scheduleExternalRefresh();
      return;
    }

    if (consumeExternalQueue) externalRefreshQueued = false;

    let showRefreshError = false;

    try {
      const sectionIds = operation.descriptors.map(({ sectionId }) => sectionId);
      const refreshURL = new URL(window.location.href);
      refreshURL.searchParams.delete('section_id');
      refreshURL.searchParams.set('sections', sectionIds.join(','));
      refreshURL.searchParams.sort();

      const response = await fetch(refreshURL.toString(), {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });
      const sections = await readJSONResponse(response);
      const validatedSections = validateSectionBundle(sections, operation.descriptors);

      await this.#applySectionBundle(validatedSections, operation.descriptors, operation.snapshots);
      this.#publishAuthoritativeUpdate({ sections }, sections, validatedSections);
    } catch {
      showRefreshError = true;
    } finally {
      this.#finishOperation(operation.descriptors);
      if (showRefreshError) this.#showRefreshError();
      CartItemsComponent.#scheduleExternalRefresh();
    }
  }

  /**
   * Captures only each cart reconciliation target before busy state is applied. For the
   * drawer this excludes the header custom element, dialog shell, and top-layer state.
   *
   * @returns {{descriptors: Array<{sectionId: string, isDrawer: boolean}>, snapshots: Map<string, {html: string, parent: Element | null, previousSibling: ChildNode | null, nextSibling: ChildNode | null}>} | null}
   */
  #prepareOperation() {
    if (cartOperationPending || !this.isConnected) return null;

    try {
      const descriptors = this.#getMountedSectionDescriptors();
      const snapshots = new Map();

      for (const descriptor of descriptors) {
        const { sectionId } = descriptor;
        const sectionRoot = document.getElementById(`shopify-section-${sectionId}`);
        if (!(sectionRoot instanceof HTMLElement)) throw new Error('Mounted cart section root missing');
        const target = resolveCartRenderTarget(sectionRoot, descriptor);
        snapshots.set(sectionId, {
          html: target.outerHTML,
          parent: target.parentElement,
          previousSibling: target.previousSibling,
          nextSibling: target.nextSibling,
        });
      }

      return { descriptors, snapshots };
    } catch {
      this.#showRefreshError();
      return null;
    }
  }

  /**
   * @returns {Array<{sectionId: string, isDrawer: boolean}>}
   */
  #getMountedSectionDescriptors() {
    const descriptors = new Map();

    for (const component of document.querySelectorAll(CART_COMPONENT_SELECTOR)) {
      if (!(component instanceof HTMLElement)) continue;
      const { sectionId } = component.dataset;
      if (!sectionId || descriptors.has(sectionId)) continue;

      descriptors.set(sectionId, {
        sectionId,
        isDrawer: component.dataset.drawer !== undefined,
      });
    }

    if (this.isConnected && !descriptors.has(this.sectionId)) {
      descriptors.set(this.sectionId, { sectionId: this.sectionId, isDrawer: this.isDrawer });
    }

    return Array.from(descriptors.values());
  }

  /**
   * @param {Array<{sectionId: string, isDrawer: boolean}>} descriptors
   * @returns {boolean}
   */
  #beginOperation(descriptors) {
    if (cartOperationPending) return false;

    cartOperationPending = true;
    this.#debouncedOnChange.cancel();

    for (const component of this.#getLiveComponents(descriptors)) {
      component.classList.add('cart-items-disabled');
      component.setAttribute('aria-busy', 'true');

      for (const control of component.querySelectorAll(CART_CONTROL_SELECTOR)) {
        if (!('disabled' in control) || control.disabled) continue;
        control.disabled = true;
        this.#disabledControls.add(control);
      }
    }

    return true;
  }

  /**
   * @param {Array<{sectionId: string, isDrawer: boolean}>} descriptors
   */
  #finishOperation(descriptors) {
    for (const control of this.#disabledControls) {
      if (control instanceof HTMLElement && control.isConnected && 'disabled' in control) {
        control.disabled = false;
      }
    }
    this.#disabledControls.clear();

    for (const component of this.#getLiveComponents(descriptors)) {
      component.classList.remove('cart-items-disabled');
      component.removeAttribute('aria-busy');
      resetShimmer(component);
    }

    if (this.isConnected) resetShimmer(this);
    cartOperationPending = false;
  }

  /**
   * @param {Array<{sectionId: string, isDrawer: boolean}>} descriptors
   * @returns {HTMLElement[]}
   */
  #getLiveComponents(descriptors) {
    const sectionIds = new Set(descriptors.map(({ sectionId }) => sectionId));

    return Array.from(document.querySelectorAll(CART_COMPONENT_SELECTOR)).filter(
      (component) =>
        component instanceof HTMLElement &&
        typeof component.dataset.sectionId === 'string' &&
        sectionIds.has(component.dataset.sectionId)
    );
  }

  /**
   * @param {Map<string, {document: Document, returnedTarget: HTMLElement, expectedSignature: string}>} validatedSections
   * @param {Array<{sectionId: string, isDrawer: boolean}>} descriptors
   * @param {Map<string, {html: string, parent: Element | null, previousSibling: ChildNode | null, nextSibling: ChildNode | null}>} snapshots
   */
  async #applySectionBundle(validatedSections, descriptors, snapshots) {
    try {
      for (const descriptor of descriptors) {
        const { sectionId } = descriptor;
        const validatedSection = validatedSections.get(sectionId);
        const liveSectionRoot = document.getElementById(`shopify-section-${sectionId}`);
        if (!validatedSection || !(liveSectionRoot instanceof HTMLElement)) {
          throw new Error(`Cart section ${sectionId} could not be reconciled`);
        }

        const liveTarget = resolveCartRenderTarget(liveSectionRoot, descriptor);
        const { returnedTarget, expectedSignature } = validatedSection;
        if (liveTarget.tagName !== returnedTarget.tagName) {
          throw new Error(`Cart section ${sectionId} changed reconciliation targets`);
        }

        const proofToken = `${sectionId}:${++morphProofSequence}`;
        returnedTarget.setAttribute(MORPH_PROOF_ATTRIBUTE, proofToken);

        let morphResult;
        try {
          morphResult = await morph(liveTarget, returnedTarget, {
            ...MORPH_OPTIONS,
            hydrationMode: false,
            childrenOnly: false,
          });

          if (morphResult !== liveTarget || liveTarget.getAttribute(MORPH_PROOF_ATTRIBUTE) !== proofToken) {
            throw new Error(`Cart section ${sectionId} did not apply its returned target`);
          }
        } finally {
          returnedTarget.removeAttribute(MORPH_PROOF_ATTRIBUTE);
          liveTarget.removeAttribute(MORPH_PROOF_ATTRIBUTE);
        }

        if (getCartTargetSignature(liveTarget, descriptor) !== expectedSignature) {
          throw new Error(`Cart section ${sectionId} did not reach the returned authoritative state`);
        }
      }
    } catch (error) {
      this.#restoreTargetSnapshots(snapshots, descriptors);
      throw error;
    }
  }

  /**
   * Restores only the cart reconciliation targets if any target morph throws or rejects.
   * Drawer/header/dialog shell identity and runtime state remain outside this boundary.
   *
   * @param {Map<string, {html: string, parent: Element | null, previousSibling: ChildNode | null, nextSibling: ChildNode | null}>} snapshots
   * @param {Array<{sectionId: string, isDrawer: boolean}>} descriptors
   */
  #restoreTargetSnapshots(snapshots, descriptors) {
    let rollbackFailure;

    for (const descriptor of descriptors) {
      const snapshot = snapshots.get(descriptor.sectionId);
      if (!snapshot) continue;

      try {
        const snapshotDocument = new DOMParser().parseFromString(snapshot.html, 'text/html');
        const snapshotTarget = snapshotDocument.body.firstElementChild;
        if (!(snapshotTarget instanceof HTMLElement)) throw new Error('Cart target snapshot was invalid');

        if (descriptor.isDrawer) {
          if (snapshotTarget.getAttribute(HYDRATION_KEY_ATTRIBUTE) !== DRAWER_HYDRATION_KEY) {
            throw new Error('Drawer target snapshot had an invalid hydration key');
          }
          findExpectedCartComponent(snapshotTarget, descriptor.sectionId);
        } else {
          findExpectedCartComponent(snapshotTarget, descriptor.sectionId);
        }

        const liveSectionRoot = document.getElementById(`shopify-section-${descriptor.sectionId}`);
        if (!(liveSectionRoot instanceof HTMLElement)) throw new Error('Live cart section missing during rollback');
        if (!(snapshot.parent instanceof HTMLElement) || !snapshot.parent.isConnected) {
          throw new Error('Cart target rollback boundary was lost');
        }
        if (!liveSectionRoot.contains(snapshot.parent)) throw new Error('Cart rollback boundary left its section');
        if (snapshot.previousSibling && snapshot.previousSibling.parentNode !== snapshot.parent) {
          throw new Error('Cart rollback start anchor was lost');
        }
        if (snapshot.nextSibling && snapshot.nextSibling.parentNode !== snapshot.parent) {
          throw new Error('Cart rollback end anchor was lost');
        }

        let residue = snapshot.previousSibling ? snapshot.previousSibling.nextSibling : snapshot.parent.firstChild;
        while (residue && residue !== snapshot.nextSibling) {
          const nextResidue = residue.nextSibling;
          snapshot.parent.removeChild(residue);
          residue = nextResidue;
        }

        snapshot.parent.insertBefore(snapshotTarget, snapshot.nextSibling);

        const restoredTarget = resolveCartRenderTarget(liveSectionRoot, descriptor);
        if (restoredTarget !== snapshotTarget) throw new Error('Cart target rollback did not restore one exact target');
      } catch (error) {
        rollbackFailure ??= error;
      }
    }

    if (rollbackFailure) throw rollbackFailure;
  }

  /**
   * @param {Record<string, any>} resource
   * @param {Record<string, string>} sections
   * @param {Map<string, {document: Document, returnedTarget: HTMLElement, expectedSignature: string}>} validatedSections
   */
  #publishAuthoritativeUpdate(resource, sections, validatedSections) {
    const currentSectionDocument = validatedSections.get(this.sectionId)?.document;
    const hiddenItemCount = currentSectionDocument?.querySelector('[ref="cartItemCount"]')?.textContent;
    const parsedItemCount = hiddenItemCount ? Number.parseInt(hiddenItemCount, 10) : 0;
    const itemCount = Number.isNaN(parsedItemCount) ? 0 : parsedItemCount;

    this.#updateQuantitySelectors(resource);
    this.#updateCartQuantitySelectorButtonStates();

    const eventSource = this.#findLiveComponent(this.sectionId);
    eventSource?.dispatchEvent(
      new CartUpdateEvent(resource, this.sectionId, {
        itemCount,
        source: 'cart-items-component',
        sections,
      })
    );
  }

  /**
   * @param {number} line
   */
  #startShimmer(line) {
    this.refs.cartTotal?.shimmer();

    const lineItemRow = this.refs.cartItemRows?.[line - 1];
    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow?.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * @param {number} line
   */
  #hideLineError(line) {
    const component = this.#findLiveComponent(this.sectionId);
    const error = component?.querySelector(`[ref="cartItemError-${line}"]`);
    const container = component?.querySelector(`[ref="cartItemErrorContainer-${line}"]`);

    if (error) error.textContent = '';
    container?.classList.add('hidden');
  }

  /**
   * Handles Shopify validation errors while retaining the existing line alert.
   * @param {number} line
   * @param {Record<string, any>} parsedResponse
   * @returns {HTMLInputElement}
   */
  #handleCartError(line, parsedResponse) {
    const component = this.#findLiveComponent(this.sectionId);
    const quantityInput = component?.querySelector(`input[data-cart-line="${line}"]`);
    const cartItemError = component?.querySelector(`[ref="cartItemError-${line}"]`);
    const cartItemErrorContainer = component?.querySelector(`[ref="cartItemErrorContainer-${line}"]`);

    if (!(quantityInput instanceof HTMLInputElement)) throw new Error('Quantity input not found');
    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    quantityInput.value = quantityInput.defaultValue;

    const errorText =
      typeof parsedResponse.errors === 'string'
        ? parsedResponse.errors
        : typeof parsedResponse.description === 'string'
          ? parsedResponse.description
          : typeof parsedResponse.message === 'string'
            ? parsedResponse.message
            : '';
    if (!errorText) throw new Error('Cart validation error text missing');

    cartItemError.textContent = errorText;
    cartItemErrorContainer.classList.remove('hidden');
    return quantityInput;
  }

  /**
   * @param {number} line
   */
  #restoreQuantityInput(line) {
    const component = this.#findLiveComponent(this.sectionId);
    const quantityInput = component?.querySelector(`input[data-cart-line="${line}"]`);
    if (quantityInput instanceof HTMLInputElement) quantityInput.value = quantityInput.defaultValue;
  }

  #hideRefreshError() {
    const component = this.#findLiveComponent(this.sectionId);
    const error = component?.querySelector('[ref="cartRefreshError"]');
    if (error instanceof HTMLElement) error.hidden = true;
  }

  #showRefreshError() {
    const component = this.#findLiveComponent(this.sectionId);
    const error = component?.querySelector('[ref="cartRefreshError"]');
    const retryButton = component?.querySelector('[ref="cartRefreshRetry"]');

    if (error instanceof HTMLElement) error.hidden = false;
    if (retryButton instanceof HTMLButtonElement) retryButton.focus();
  }

  /**
   * @param {string} sectionId
   * @returns {HTMLElement | undefined}
   */
  #findLiveComponent(sectionId) {
    return Array.from(document.querySelectorAll(CART_COMPONENT_SELECTOR)).find(
      (component) => component instanceof HTMLElement && component.dataset.sectionId === sectionId
    );
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Record<string, any>} updatedCart - The updated cart object.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!Array.isArray(updatedCart.items)) return;

    for (const item of updatedCart.items) {
      if (!isRecord(item) || typeof item.variant_id !== 'number' || typeof item.quantity !== 'number') continue;

      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
